import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { usePrivacy } from "@/context/PrivacyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCurrency } from "@/lib/format";
import { FileSpreadsheet, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const Money = ({ v, mask, cls = "" }) => <td className={`px-2 sm:px-3 py-2 text-right num ${cls}`}>{mask(fmtCurrency(v, { decimals: 2 }))}</td>;

const Growth = ({ v, ytd, testId }) => v == null ? <span className="text-muted-foreground">—</span> : (
    <span className={`num ${v >= 0 ? "text-gain" : "text-loss"}`} data-testid={testId} title={ytd ? "Year to date vs. full prior year" : "Gross income vs. prior year"}>
        {v >= 0 ? "+" : ""}{v.toFixed(1)}%{ytd ? " YTD" : ""}
    </span>
);

// Gross amount; on phones the growth % sits underneath instead of in its own column
const GrossCell = ({ v, g, ytd, mask, testId }) => (
    <td className="px-2 sm:px-3 py-2 text-right num">
        {mask(fmtCurrency(v, { decimals: 2 }))}
        <div className="sm:hidden text-[10px] leading-tight"><Growth v={g} ytd={ytd} testId={`${testId}-m`} /></div>
    </td>
);
const GrowthCell = ({ v, ytd, testId }) => <td className="hidden sm:table-cell px-3 py-2 text-right"><Growth v={v} ytd={ytd} testId={testId} /></td>;

function SubRows({ s, year, ytd, mask }) {
    const [open, setOpen] = useState(false);
    const id = `${year}-${s.sub_portfolio}`;
    return (
        <>
            <tr className="border-t border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid={`annual-sub-row-${id}`}>
                <td className="px-2 sm:px-3 py-2"><span className="inline-flex items-center gap-1.5"><ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />{s.sub_portfolio}</span></td>
                <td className="hidden sm:table-cell px-3 py-2 text-right num text-muted-foreground">{s.payments}</td>
                <GrossCell v={s.gross} g={s.growth_pct} ytd={ytd} mask={mask} testId={`annual-sub-growth-${id}`} /><Money v={s.tax} mask={mask} cls="text-muted-foreground" /><Money v={s.net} mask={mask} cls="font-medium" />
                <GrowthCell v={s.growth_pct} ytd={ytd} testId={`annual-sub-growth-${id}`} />
            </tr>
            {open && s.stocks.map((r) => (
                <tr key={r.ticker} className="text-xs bg-muted/20" data-testid={`annual-stock-row-${id}-${r.ticker}`}>
                    <td className="pl-6 sm:pl-9 pr-2 sm:pr-3 py-1.5"><span className="font-semibold num">{r.ticker}</span> <span className="hidden sm:inline text-muted-foreground truncate">{r.name !== r.ticker ? r.name : ""}</span></td>
                    <td className="hidden sm:table-cell px-3 py-1.5 text-right num text-muted-foreground">{r.payments}</td>
                    <Money v={r.gross} mask={mask} /><Money v={r.tax} mask={mask} cls="text-muted-foreground" /><Money v={r.net} mask={mask} /><td className="hidden sm:table-cell" />
                </tr>
            ))}
        </>
    );
}

export function AnnualIncomeReport({ sub }) {
    const { mask } = usePrivacy();
    const [year, setYear] = useState("ALL");
    const [busy, setBusy] = useState(false);
    const { data, isLoading } = useQuery({ queryKey: ["div-annual", sub], queryFn: async () => (await api.get(`/dividends/annual-report?sub=${sub}`)).data });
    const years = data?.years || [];
    const shown = year === "ALL" ? years : years.filter((y) => y.year === year);

    const exportXlsx = async () => {
        setBusy(true);
        try {
            const res = await api.get(`/dividends/annual-report/export?sub=${encodeURIComponent(sub)}&year=${year}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `dividend_income_${year === "ALL" ? "all_years" : year}.xlsx`; a.click(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    if (isLoading) return <Skeleton className="h-40 rounded-xl" />;
    if (!years.length) return null;
    return (
        <Card className="p-3 sm:p-5" data-testid="annual-income-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                    <h2 className="font-heading text-lg font-semibold">Annual Income Report</h2>
                    <p className="text-sm text-muted-foreground">Dividends by pay year and sub-portfolio, with tax withheld and gross growth vs. the prior year. Click a sub-portfolio for each stock.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={year} onValueChange={setYear}>
                        <SelectTrigger className="w-32" data-testid="annual-year-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All years</SelectItem>
                            {years.map((y) => <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={exportXlsx} disabled={busy} data-testid="annual-export-btn">
                        <FileSpreadsheet className="w-4 h-4 mr-1.5" />{busy ? "Exporting…" : "Export Excel"}
                    </Button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm sm:min-w-[520px]" data-testid="annual-income-table">
                    <thead><tr className="text-xs text-muted-foreground uppercase tracking-wide">
                        <th className="px-3 py-2 text-left font-medium">Sub-portfolio</th><th className="hidden sm:table-cell px-3 py-2 text-right font-medium">Payments</th>
                        <th className="px-3 py-2 text-right font-medium">Gross</th><th className="px-3 py-2 text-right font-medium"><span className="sm:hidden">Tax</span><span className="hidden sm:inline">Tax Withheld</span></th><th className="px-3 py-2 text-right font-medium">Net</th><th className="hidden sm:table-cell px-3 py-2 text-right font-medium">Growth</th>
                    </tr></thead>
                    <tbody>
                        {shown.map((y) => (
                            <Fragment key={y.year}>
                                <tr className="bg-primary/5 font-semibold border-t border-border" data-testid={`annual-year-row-${y.year}`}>
                                    <td className="px-2 sm:px-3 py-2 font-heading">{y.year}</td>
                                    <td className="hidden sm:table-cell px-3 py-2 text-right num">{y.payments}</td>
                                    <GrossCell v={y.gross} g={y.growth_pct} ytd={y.ytd} mask={mask} testId={`annual-year-growth-${y.year}`} /><Money v={y.tax} mask={mask} /><Money v={y.net} mask={mask} cls="text-primary" />
                                    <GrowthCell v={y.growth_pct} ytd={y.ytd} testId={`annual-year-growth-${y.year}`} />
                                </tr>
                                {y.subs.map((s) => <SubRows key={s.sub_portfolio} s={s} year={y.year} ytd={y.ytd} mask={mask} />)}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}
